/**
 * User roles for access control
 * 
 * Role Hierarchy (highest to lowest privilege):
 * - SUPER_ADMIN: Full system access, can manage all organizations
 * - ADMIN: Organization admin, can manage users within their organizations
 * - OPERATOR: Can view and operate on assigned organizations
 * - VIEWER: Read-only access to assigned organizations
 * 
 * Legacy roles (for backward compatibility):
 * - CLIENT_ADMIN: Mapped to ADMIN
 * - USER: Mapped to VIEWER
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  OPERATOR = 'operator',
  VIEWER = 'viewer',
  // Legacy roles (deprecated, mapped on load)
  CLIENT_ADMIN = 'clientAdmin',
  USER = 'user',
}

/**
 * Map legacy roles to new roles
 */
export function normalizeRole(role: string): UserRole {
  switch (role) {
    case 'clientAdmin':
      return UserRole.ADMIN;
    case 'user':
      return UserRole.VIEWER;
    case 'super_admin':
    case 'superAdmin':
      return UserRole.SUPER_ADMIN;
    case 'admin':
      return UserRole.ADMIN;
    case 'operator':
      return UserRole.OPERATOR;
    case 'viewer':
      return UserRole.VIEWER;
    default:
      return UserRole.VIEWER;
  }
}

/**
 * Check if role has at least the minimum required privilege
 */
export function hasMinimumRole(userRole: UserRole, minRole: UserRole): boolean {
  const roleHierarchy = [UserRole.VIEWER, UserRole.OPERATOR, UserRole.ADMIN, UserRole.SUPER_ADMIN];
  const userLevel = roleHierarchy.indexOf(normalizeRole(userRole));
  const minLevel = roleHierarchy.indexOf(normalizeRole(minRole));
  return userLevel >= minLevel;
}

/**
 * User profile stored in Firestore / returned by backend
 */
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: UserRole;
  /** CMS organization IDs this user can access (from Firebase custom claims) */
  companies: number[];
  /** True when the account is meant for testing/non-production use */
  isTesting?: boolean;
  /** @deprecated Use companies array instead */
  organizationId: string | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string | null; // UID of the user who created this account
  isActive: boolean;
  lastLoginAt: Date | null;
  disabled?: boolean;
  emailVerified?: boolean;
}

/**
 * User data returned by backend API
 */
export interface ApiUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: string;
  companies: number[];
  isTesting: boolean;
  createdBy: string | null;
  createdAt: string;
  lastSignInAt: string | null;
  disabled: boolean;
  emailVerified: boolean;
}

/**
 * Request to create a new user
 */
export interface CreateUserRequest {
  email: string;
  displayName?: string;
  password?: string;
  role: 'viewer' | 'operator' | 'admin';
  companies: number[];
  sendPasswordReset?: boolean;
}

/**
 * Request to update a user
 */
export interface UpdateUserRequest {
  displayName?: string;
  password?: string;
  role?: UserRole;
  companies?: number[];
  disabled?: boolean;
}

/**
 * Company/Organization returned by backend
 */
export interface Company {
  id: number;
  name: string;
  cmsId: number;
}

/**
 * Address structure for organizations
 */
export interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

/**
 * Organization/Company structure
 */
export interface Organization {
  id: string;
  name: string;
  slug: string; // URL-friendly identifier
  logoUrl: string | null;

  // Contact information
  contactEmail: string | null;
  contactPhone: string | null;
  address: Address | null;

  // CMS Integration (link to monitor-backend)
  cmsOrganizationId: number | null;

  // Ownership
  ownerId: string; // Client admin who owns this org

  // Settings
  settings: OrganizationSettings;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

/**
 * Organization settings
 */
export interface OrganizationSettings {
  maxUsers: number;
  features: string[]; // Enabled features
  timezone: string;
  dateFormat: string;
  notificationsEnabled: boolean;
  branding?: {
    logoUrl?: string;
    primaryColor?: string;
  };
}

/**
 * Auth state for the application
 */
export interface AuthState {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Create sub-account request
 */
export interface CreateSubAccountRequest {
  email: string;
  displayName: string;
  role: UserRole;
  organizationId: string;
}
